import React from "react";
import { string, arrayOf, shape, func, object, bool } from "prop-types";
import { Link, ResponsiveImage } from "@quintype/components";
import PlusIcon from "../../atoms/PlusIcon";
import MinusIcon from "../../atoms/MinusIcon";
import TeamLinkedInIcon from "../../atoms/TeamLinkedInIcon";

import "./team-card.m.css";

/* TeamCard molecule - displays team member with expandable bio */
const TeamCard = ({ member, onMemberClick, onToggleExpand, isExpanded = false, variant = "default" }) => {
  const { id, name, designation, imageSlug, imageMetadata, image, linkedIn, bio } = member;

  const handleToggleExpand = (e) => {
    e.stopPropagation();
    if (bio?.length > 0 && onToggleExpand) {
      onToggleExpand(isExpanded ? null : id);
    }
  };

  const handleCardClick = () => {
    if (onMemberClick) {
      onMemberClick(member);
    }
  };

  const hasBio = bio?.length > 0;
  const cardStyleName = variant === "default" ? "team-card" : `team-card ${variant}`;

  /* Renders member image using ResponsiveImage for API data or img for static */
  const renderMemberImage = () => {
    if (imageSlug) {
      return (
        <ResponsiveImage
          slug={imageSlug}
          metadata={imageMetadata}
          aspectRatio={[328, 392]}
          defaultWidth={328}
          widths={[328, 480]}
          imgParams={{ auto: ["format", "compress"] }}
          eager="below-fold"
          alt={name || "Team member"}
        />
      );
    }
    return (
      <img
        src={image || "/icons/team/placeholder.png"}
        alt={name}
        styleName="member-image"
        loading="lazy"
      />
    );
  };

  return (
    <article styleName={cardStyleName}>
      <div styleName="image-container">
        {renderMemberImage()}
        {hasBio && (
          <button
            styleName={`expand-btn ${isExpanded ? "collapse" : ""}`}
            onClick={handleToggleExpand}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse bio" : "Expand bio"}
          >
            {isExpanded ? <MinusIcon /> : <PlusIcon />}
          </button>
        )}
        {isExpanded && hasBio && (
          <div styleName="bio-overlay">
            <div styleName="bio-content">
              {bio.map((paragraph, idx) => (
                <p key={idx} styleName="bio-paragraph">
                  {paragraph.highlight && (
                    <span styleName="bio-highlight">{paragraph.highlight}</span>
                  )}
                  {paragraph.text}
                </p>
              ))}
            </div>
            <button styleName="read-more-link" onClick={handleCardClick}>
              Read more
            </button>
            <button
              styleName="collapse-btn"
              onClick={handleToggleExpand}
              aria-label="Collapse bio"
            >
              <MinusIcon />
            </button>
          </div>
        )}
      </div>
      <div styleName="info-section">
        <div styleName="text-content">
          <h3 styleName="member-name">{name}</h3>
          <p styleName="member-designation">{designation}</p>
        </div>
        {linkedIn && (
          <Link
            href={linkedIn}
            styleName="linkedin-link"
            aria-label={`${name}'s LinkedIn profile`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <TeamLinkedInIcon color="currentColor" />
          </Link>
        )}
      </div>
    </article>
  );
};

TeamCard.propTypes = {
  member: shape({
    id: string.isRequired,
    name: string.isRequired,
    designation: string,
    imageSlug: string,
    imageMetadata: object,
    image: string,
    linkedIn: string,
    bio: arrayOf(shape({
      highlight: string,
      text: string.isRequired
    }))
  }).isRequired,
  onMemberClick: func,
  onToggleExpand: func,
  isExpanded: bool,
  variant: string
};

export default TeamCard;
